import { EmployeeJobLevelEnum } from 'common/constants/enum';
import Separator from 'components/common/separator';
import { useFormState } from 'components/elements/form';
import jobLevelOptions from 'components/elements/table/editable-columns/job-level-options';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import Text from 'components/elements/text';
import { CardContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import {
  BonusPeriodFormType,
  EmployeeJobLevelConfigurationFormType,
} from './form-type';
import {
  generateDividenPercentage,
  generatePercentage,
  generateJobLevelByEmployees,
  recalculateJobLevelConfigures,
} from '../hooks/use-get-default-values';

const name = 'employeeJobLevelConfigurations' as const;

function useGetJobLevelTableList(): IColumn<EmployeeJobLevelConfigurationFormType>[] {
  const { t } = useTranslation();
  return [
    {
      header: t('common:job_level'),
      accessorKey: 'jobLevel',
      dataType: ColumnDataType.select,
      options: jobLevelOptions(t),
      disabled: true,
      maxSize: 100,
    },
    {
      header: t('common:default_percentage'),
      accessorKey: 'percentage',
      dataType: ColumnDataType.number,
      minNumber: 0,
      maxSize: 150,
    },
    {
      header: t('common:count'),
      accessorKey: 'count',
      dataType: ColumnDataType.number,
      minNumber: 0,
      disabled: true,
      maxSize: 80,
    },
    {
      header: t('common:percentage'),
      accessorKey: 'dividenPercentage',
      dataType: ColumnDataType.number,
      minNumber: 0,
      maxSize: 80,
    },
  ];
}

export default function JobLevelTableInput() {
  const { editable } = useFormState();
  const { t } = useTranslation();
  const { getValues, clearErrors, control } =
    useFormContext<BonusPeriodFormType>();

  const { fields, replace } = useFieldArray({
    name,
    control,
  });

  const [employees] = useWatch({
    name: ['employees'],
  });

  const columns = useGetJobLevelTableList();

  React.useEffect(() => {
    const employeeJobLevelConfigurations = getValues(name);
    const epc = generateJobLevelByEmployees(
      employeeJobLevelConfigurations,
      employees,
    );
    replace(epc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);

  const dispatch = React.useCallback(
    (action) => {
      const jobLevels = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const jobLevel = jobLevels[currentRowIndex];
      const type = action.type as ActionType;
      const columnId =
        action.columnId as keyof EmployeeJobLevelConfigurationFormType;
      const value = action.value;

      const otherVal: () => Partial<EmployeeJobLevelConfigurationFormType> =
        () => {
          if (typeof value !== 'number') return {};
          if (columnId === 'dividenPercentage') {
            const percentage = generatePercentage(value, jobLevel.count);
            return {
              percentage,
            };
          } else if (columnId === 'percentage') {
            const dividenPercentage = generateDividenPercentage(
              value,
              jobLevel.count,
            );

            return {
              dividenPercentage,
            };
          }
          return {};
        };

      if (type === 'update_cell') {
        jobLevels[currentRowIndex] = {
          ...jobLevel,
          [columnId]: value,
          ...otherVal(),
        };
        const _jobLevels = recalculateJobLevelConfigures(jobLevels);
        replace(_jobLevels);
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [clearErrors, getValues, replace],
  );

  return (
    <CardContainer>
      <Text variant="LabelLarge">{t('common:job_level')}</Text>
      <Separator gap={16} />
      <TableInput
        columns={columns}
        data={fields}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
        onCustomDisabledCell={({ rowOriginal }) => {
          return rowOriginal.jobLevel === EmployeeJobLevelEnum.Director;
        }}
        noAddRemoveRow
      />
    </CardContainer>
  );
}
